import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

const CreditsEGiftPreview = ({ isOpen, onClose, onAddToCart, creditPackage }) => {
  const { t } = useTranslation();
  const [currentName, setCurrentName] = useState(0);
  const sampleNames = ['Sarah', 'Michael', 'Emma', 'David', 'Jennifer', 'Alex'];
  const [showSparkles, setShowSparkles] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Rotate through sample names
      const nameInterval = setInterval(() => {
        setCurrentName(prev => (prev + 1) % sampleNames.length);
      }, 2500);

      // Sparkle animation
      const sparkleInterval = setInterval(() => {
        setShowSparkles(true);
        setTimeout(() => setShowSparkles(false), 1000);
      }, 3000);

      return () => {
        clearInterval(nameInterval);
        clearInterval(sparkleInterval);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      style={{ 
        zIndex: 3000,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
      onClick={onClose}
    >
      <div 
        className="relative max-w-2xl w-full mx-auto my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Postcard Container */}
        <div className="bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border-2 sm:border-4 border-white transform hover:scale-[1.02] transition-transform relative">
          {/* Close Button - Inside card on mobile */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-20 group"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 group-hover:text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Decorative Stamp */}
          <div className="absolute top-6 right-6 w-20 h-20 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-lg rotate-12 opacity-20 flex items-center justify-center">
            <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>

          {/* Sparkles Animation */}
          {showSparkles && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(15)].map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-sparkle"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                  }}
                >
                  ✨
                </div>
              ))}
            </div>
          )}

          {/* Card Content */}
          <div className="p-4 sm:p-8 md:p-12 relative">
            {/* Header */}
            <div className="text-center mb-4 sm:mb-8">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                Magic Moments
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 font-medium">{t('egift.egift_card')}</p>
            </div>

            {/* Main Gift Card Visual */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 mb-4 sm:mb-6 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle, #8b5cf6 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>

              {/* Gift Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-full flex items-center justify-center shadow-lg animate-bounce-slow">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              {/* Credit Amount */}
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                  {creditPackage?.credits || '50'}
                </div>
                <div className="text-xl sm:text-2xl text-gray-600 font-semibold">{t('egift.credits')}</div>
                <div className="text-xs sm:text-sm text-gray-500 mt-2">
                  ≈ {Math.floor((creditPackage?.credits || 50) / 5)} AI-Powered Videos
                </div>
              </div>

              {/* Animated Gift For Section */}
              <div className="text-center py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-teal-100 to-emerald-100 rounded-xl">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">{t('egift.gift_for')}</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-800 h-8 flex items-center justify-center">
                  <span key={currentName} className="animate-slide-up">
                    {sampleNames[currentName]}
                  </span>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="bg-purple-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-2xl mb-1">🎬</div>
                <div className="text-[10px] sm:text-xs text-purple-800 font-semibold">{t('egift.ai_videos')}</div>
              </div>
              <div className="bg-blue-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-2xl mb-1">⚡</div>
                <div className="text-[10px] sm:text-xs text-blue-800 font-semibold">{t('egift.instant_delivery')}</div>
              </div>
              <div className="bg-indigo-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-2xl mb-1">♾️</div>
                <div className="text-[10px] sm:text-xs text-indigo-800 font-semibold">{t('egift.never_expires')}</div>
              </div>
              <div className="bg-purple-100 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
                <div className="text-xl sm:text-2xl mb-1">💝</div>
                <div className="text-[10px] sm:text-xs text-purple-800 font-semibold">{t('egift.perfect_gift')}</div>
              </div>
            </div>

            {/* Price Display */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-2xl sm:text-3xl font-bold text-gray-800">
                ${creditPackage?.price || '9.99'}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">{t('egift.one_time_purchase')}</div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => {
                onAddToCart && onAddToCart();
                onClose();
              }}
              className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg shadow-lg transform hover:scale-105 transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Add to Cart
            </button>

            {/* Note */}
            <p className="text-xs text-center text-gray-500 mt-4">
              Configure recipient details at checkout
            </p>
          </div>
        </div>

        {/* CSS for animations */}
        <style jsx>{`
          @keyframes slide-up {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            20% {
              opacity: 1;
              transform: translateY(0);
            }
            80% {
              opacity: 1;
              transform: translateY(0);
            }
            100% {
              opacity: 0;
              transform: translateY(-20px);
            }
          }
          
          @keyframes sparkle {
            0%, 100% {
              opacity: 0;
              transform: scale(0) rotate(0deg);
            }
            50% {
              opacity: 1;
              transform: scale(1) rotate(180deg);
            }
          }
          
          @keyframes bounce-slow {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          
          .animate-slide-up {
            animation: slide-up 2.5s ease-in-out;
          }
          
          .animate-sparkle {
            animation: sparkle 1s ease-out forwards;
          }
          
          .animate-bounce-slow {
            animation: bounce-slow 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CreditsEGiftPreview;
